import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import type { CheckoutOrder, DeliverySlot } from '@/api/orders'
import { cartApi } from '@/api/orders'
import { AppShell } from '@/components/AppShell'
import { Alert, BackLink, Button, Field } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { CutoffCountdown } from '@/features/delivery/DeliveryWindow'
import { formatDate } from '@/lib/format'

const SLOTS: DeliverySlot[] = ['morning', 'noon', 'afternoon', 'custom']

export default function CheckoutPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const translateError = useApiError()

  const [date, setDate] = useState('')
  const [slot, setSlot] = useState<DeliverySlot | ''>('morning')
  const [time, setTime] = useState('')
  const [note, setNote] = useState('')
  const [sent, setSent] = useState<CheckoutOrder[] | null>(null)
  /** Προμηθευτές που ο χρήστης κράτησε για αργότερα. Αρχικά στέλνονται όλοι. */
  const [held, setHeld] = useState<Set<string>>(new Set())
  const [kept, setKept] = useState(0)

  const cart = useQuery({ queryKey: ['cart'], queryFn: cartApi.get })
  const groups = cart.data?.groups ?? []
  const selected = groups.filter((group) => !held.has(group.supplierId))
  const toggleHeld = (supplierId: string) =>
    setHeld((current) => {
      const next = new Set(current)
      if (next.has(supplierId)) next.delete(supplierId)
      else next.add(supplierId)
      return next
    })

  // Η προεπιλογή είναι η νωρίτερη ημέρα που εξυπηρετεί όλους τους προμηθευτές
  // του καλαθιού. Χωρίς αυτό η φόρμα θα πρότεινε «αύριο» ακόμη και όταν το
  // αύριο έχει ήδη αποκλειστεί από το cutoff.
  const earliest = cart.data?.earliestDeliveryDate ?? null
  useEffect(() => {
    if (earliest && !date) setDate(earliest)
  }, [earliest, date])

  // Οι ημέρες που δεν παραδίδει κάποιος από τους προμηθευτές που στέλνονται τώρα.
  const blocked = selected
    .filter((group) => group.delivery && group.delivery.deliveryDays.length > 0)
    .map((group) => ({
      name: group.supplierName,
      days: new Set(group.delivery!.deliveryDays),
    }))

  const weekday = date ? (new Date(`${date}T12:00:00`).getDay() + 6) % 7 : null
  const conflict =
    weekday === null ? null : blocked.find((entry) => !entry.days.has(weekday))?.name ?? null

  const checkout = useMutation({
    mutationFn: () =>
      cartApi.checkout({
        requestedDeliveryDate: date || undefined,
        requestedDeliverySlot: slot || undefined,
        requestedDeliveryTime: slot === 'custom' && time ? `${time}:00` : undefined,
        orderNote: note.trim() || undefined,
        supplierIds: held.size > 0 ? selected.map((group) => group.supplierId) : undefined,
      }),
    onSuccess: (result) => {
      setKept(groups.length - selected.length)
      setSent(result.orders)
      void queryClient.invalidateQueries({ queryKey: ['cart'] })
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
  })

  // Οθόνη επιβεβαίωσης: οι N παραγγελίες με τους αριθμούς τους (§7.2, βήμα 5).
  if (sent) {
    return (
      <AppShell title={t('checkout.sentTitle')}>
        <div className="rounded-2xl border border-brand-200 bg-brand-50 p-5">
          <p className="text-lg font-semibold text-brand-900">
            {t('checkout.sentCount', { count: sent.length })}
          </p>
          <ul className="mt-3 flex flex-col gap-2">
            {sent.map((order) => (
              <li key={order.id}>
                <Link
                  to={`/orders/${order.id}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-brand-200 bg-white px-4 py-3 hover:border-brand-400"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-gray-900">{order.supplierName}</p>
                    <p className="text-xs text-gray-500">
                      {t('checkout.itemCount', { count: order.itemCount })}
                    </p>
                  </div>
                  <span className="shrink-0 font-mono text-sm font-semibold text-brand-700">
                    {order.orderNumber}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {kept > 0 && (
          <div className="mt-4">
            <Alert tone="warning">{t('checkout.keptInCart', { count: kept })}</Alert>
          </div>
        )}

        <div className="mt-4 flex flex-col gap-2">
          <Button onClick={() => navigate('/orders')}>{t('checkout.viewOrders')}</Button>
          {kept > 0 && (
            <Button variant="secondary" onClick={() => navigate('/cart')}>
              {t('checkout.backToCart')}
            </Button>
          )}
        </div>
      </AppShell>
    )
  }

  // Χωρίς είδη δεν υπάρχει τι να σταλεί: η σελίδα ανοίγει μόνο από το καλάθι,
  // αλλά ένας αποθηκευμένος σύνδεσμος ή το «πίσω» του browser φτάνουν εδώ.
  if (cart.data && cart.data.itemCount === 0) return <Navigate to="/cart" replace />

  return (
    <AppShell title={t('checkout.title')}>
      <BackLink to="/cart" label={t('common.back')} />

      {cart.data && cart.data.itemCount > 0 && groups.length < 2 && (
        <div className="mb-4 rounded-2xl border border-brand-200 bg-brand-50 p-4 text-sm font-semibold text-brand-900">
          {t('cart.willSend', { count: cart.data.orderCount })}
        </div>
      )}

      {/* Με πολλούς προμηθευτές ο χρήστης διαλέγει σε ποιους στέλνει τώρα· οι
          υπόλοιποι μένουν στο καλάθι, π.χ. όταν περιμένει να δει τι λείπει. */}
      {groups.length > 1 && (
        <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h2 className="font-semibold text-gray-900">{t('checkout.recipients')}</h2>
            {held.size > 0 && (
              <button
                type="button"
                onClick={() => setHeld(new Set())}
                className="text-sm font-semibold text-brand-700 hover:text-brand-600"
              >
                {t('checkout.selectAll')}
              </button>
            )}
          </div>
          <ul className="flex flex-col gap-1.5">
            {groups.map((group) => {
              const checked = !held.has(group.supplierId)
              return (
                <li key={group.supplierId}>
                  <label
                    className={
                      'flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border px-3 transition-colors ' +
                      (checked ? 'border-brand-300 bg-brand-50' : 'border-gray-200 bg-white')
                    }
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleHeld(group.supplierId)}
                      className="size-5 accent-brand-700"
                    />
                    <span className="min-w-0 flex-1 truncate font-medium text-gray-900">
                      {group.supplierName}
                    </span>
                    <span className="shrink-0 text-xs text-gray-500">
                      {t('checkout.itemCount', { count: group.items.length })}
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>
          <p className="mt-2 text-xs text-gray-500">{t('checkout.recipientsHint')}</p>
        </section>
      )}

      {(cart.data?.unavailableCount ?? 0) > 0 && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {t('availability.cartWarning', { count: cart.data!.unavailableCount })}
        </div>
      )}

      {/* Η αντίστροφη μέτρηση ανά προμηθευτή: το βλέμμα πέφτει εδώ πριν πατηθεί
          το κουμπί αποστολής, όχι αφού φύγει η παραγγελία. */}
      {cart.data && cart.data.groups.some((group) => group.delivery?.cutoffTime) && (
        <ul className="mb-4 flex flex-col gap-2">
          {cart.data.groups
            .filter((group) => group.delivery?.cutoffTime)
            .map((group) => (
              <li
                key={group.supplierId}
                className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3"
              >
                <span className="min-w-0 truncate text-sm font-medium text-gray-900">
                  {group.supplierName}
                </span>
                <CutoffCountdown window={group.delivery!} />
              </li>
            ))}
        </ul>
      )}

      <form
        className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4"
        onSubmit={(event) => {
          event.preventDefault()
          checkout.mutate()
        }}
      >
        {checkout.isError && <Alert>{translateError(checkout.error)}</Alert>}
        {groups.length > 0 && selected.length === 0 && (
          <Alert tone="warning">{t('checkout.noneSelected')}</Alert>
        )}

        <Field
          label={t('checkout.deliveryDate')}
          name="deliveryDate"
          type="date"
          value={date}
          min={earliest ?? today()}
          onChange={(event) => setDate(event.target.value)}
        />
        {earliest && (
          <p className="-mt-2 text-xs text-gray-500">
            {t('delivery.chooseFromEarliest', { date: formatDate(earliest) })}
          </p>
        )}
        {conflict && (
          <Alert tone="error">{t('delivery.dateBlocked', { name: conflict })}</Alert>
        )}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="slot" className="text-sm font-medium text-gray-700">
            {t('checkout.deliverySlot')}
          </label>
          <select
            id="slot"
            value={slot}
            onChange={(event) => setSlot(event.target.value as DeliverySlot | '')}
            className="h-touch rounded-xl border border-gray-300 bg-white px-3 text-base text-gray-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
          >
            <option value="">{t('checkout.noPreference')}</option>
            {SLOTS.map((value) => (
              <option key={value} value={value}>
                {t(`checkout.slots.${value}`)}
              </option>
            ))}
          </select>
        </div>

        {slot === 'custom' && (
          <Field
            label={t('checkout.deliveryTime')}
            name="deliveryTime"
            type="time"
            required
            value={time}
            onChange={(event) => setTime(event.target.value)}
          />
        )}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="note" className="text-sm font-medium text-gray-700">
            {t('checkout.orderNote')}
          </label>
          <textarea
            id="note"
            rows={3}
            maxLength={1000}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t('checkout.orderNotePlaceholder')}
            className="rounded-xl border border-gray-300 bg-white p-3 text-base text-gray-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
          />
          <p className="text-xs text-gray-500">{t('checkout.orderNoteHint')}</p>
        </div>

        <Button
          type="submit"
          loading={checkout.isPending}
          disabled={cart.data?.itemCount === 0 || selected.length === 0 || conflict !== null}
        >
          {t('checkout.submit', { count: selected.length })}
        </Button>
      </form>
    </AppShell>
  )
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}
